import { Injectable, OnDestroy } from '@angular/core';
import { forkJoin, Observable, of, Subject, tap } from 'rxjs';
import { RefDataService } from '../../services/ref-data.service';
import { Domain, Language, LocalizedKeyValue, Market } from '../models/dataref.model';
import { StateService } from './state.service';
import { catchError, map, takeUntil } from 'rxjs/operators';

export interface InitialDomainMarketLangState {
  domains: Domain[];
  markets: Market[];
  languages: Language[];
  commentReasons: LocalizedKeyValue[];
  commentStatuses: LocalizedKeyValue[];
}

const initialState: InitialDomainMarketLangState = {
  domains: [],
  markets: [],
  languages: [],
  commentReasons: [],
  commentStatuses: [],
};

@Injectable({
  providedIn: 'root',
})
export class DomainMarketLangState extends StateService<InitialDomainMarketLangState> implements OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();

  constructor(private readonly refDataService: RefDataService) {
    super(initialState);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(): Observable<InitialDomainMarketLangState> {
    return forkJoin({
      domains: this.getRefDataCollectionAsArray<Domain>('domains'),
      markets: this.getRefDataCollectionAsArray<Market>('markets'),
      languages: this.getRefDataCollectionAsArray<Language>('languages'),
      commentReasons: this.getRefDataCollectionAsArray<LocalizedKeyValue>('commentReasons'),
      commentStatuses: this.getRefDataCollectionAsArray<LocalizedKeyValue>('commentStatuses'),
    }).pipe(
      tap((newState: InitialDomainMarketLangState) => {
        this.setState(newState);
      })
    );
  }

  public getCurrentState(): Observable<InitialDomainMarketLangState> {
    return this.select((state) => state);
  }

  private getRefDataCollectionAsArray<T>(refDataCollectionName: string): Observable<T[]> {
    return this.refDataService.getByKey(refDataCollectionName).pipe(
      catchError(() => of([] as T[])),
      map((data) => this.refDataService.translateReference(this.refDataService.toArray(data)) as T[]),
      takeUntil(this.destroy$)
    );
  }
}
